function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _type_of(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
}
import { useNavigate } from "react-router";
import { useCallback, useContext } from 'react';
import { UNSAFE_NavigationContext } from "react-router";
import { getRoutePath } from '@edusense/shared-utils';
/**
 * react-router-dom의 useNavigate를 확장하여 Router context가 없는 경우에도
 * 안전하게 동작하는 navigate 함수를 제공하는 커스텀 훅입니다.
 *
 * @returns {function} 안전한 navigate 함수
 */ export function useSafeNavigate() {
    // Router context가 없을 때 사용할 fallback 함수
    var fallbackNavigate = useCallback(function(to, options) {
        console.warn("Navigation to ".concat(to, " attempted outside Router context"));
        // 숫자 인자(예: -1)인 경우 history.back() 사용
        if (typeof to === 'number') {
            if (typeof window !== 'undefined' && window.history) {
                if (to === -1) {
                    window.history.back();
                } else if (to === 1) {
                    window.history.forward();
                } else {
                    window.history.go(to);
                }
            }
            return;
        }
        // 문자열 URL인 경우 window.location 사용
        if (typeof to === 'string') {
            if (typeof window !== 'undefined') {
                // Direct navigation for relative paths
                window.location.href = to;
            }
        } else if ((typeof to === "undefined" ? "undefined" : _type_of(to)) === 'object' && to.pathname) {
            // Handle Path objects
            if (typeof window !== 'undefined') {
                var pathname = to.pathname || '';
                var search = to.search || '';
                var hash = to.hash || '';
                window.location.href = "".concat(pathname).concat(search).concat(hash);
            }
        }
    }, []);
    // Check if we're in a Router context first
    var navigationContext = useContext(UNSAFE_NavigationContext);
    var hasRouterContext = navigationContext != null;
    // If no Router context, return fallback immediately
    if (!hasRouterContext) {
        return fallbackNavigate;
    }
    // We have Router context, so it's safe to call useNavigate
    var navigate = useNavigate();
    var prefixedNavigate = useCallback(function(to, options) {
        // 숫자 인자(예: navigate(-1))는 prefix 없이 그대로 전달합니다.
        if (typeof to === 'number') {
            navigate(to);
            return;
        }
        console.log('useSafeNavigate', to);
        // 직접 navigate 호출 (prefix 로직 제거)
        navigate(to, options);
    }, [
        navigate
    ] // navigate 함수가 변경될 때만 콜백 재생성
    );
    return prefixedNavigate;
}
/**
 * 환경에 맞는 경로 접두사를 자동으로 적용하는 navigate 함수를 제공하는 훅
 * 
 * @returns {function} 접두사가 적용된 navigate 함수
 */ export function usePrefixedNavigate() {
    // Router context 체크
    var navigationContext = useContext(UNSAFE_NavigationContext);
    var hasRouterContext = navigationContext != null;
    // fallback navigate function for when there's no Router context
    var fallbackNavigate = useCallback(function(to, options) {
        console.warn("Navigation to ".concat(to, " attempted outside Router context"));
        // 숫자 인자(예: -1)인 경우 history API 사용
        if (typeof to === 'number') {
            if (typeof window !== 'undefined' && window.history) {
                if (to === -1) {
                    window.history.back();
                } else if (to === 1) {
                    window.history.forward();
                } else {
                    window.history.go(to);
                }
            }
            return;
        }
        // 문자열 URL인 경우 prefix 적용 후 window.location 사용
        if (typeof to === 'string') {
            if (typeof window !== 'undefined') {
                var prefixedPath = getRoutePath(to);
                window.location.href = prefixedPath;
            }
        } else if ((typeof to === "undefined" ? "undefined" : _type_of(to)) === 'object' && to.pathname) {
            // Handle Path objects
            if (typeof window !== 'undefined') {
                var prefixedPath1 = getRoutePath(to.pathname || '');
                var search = to.search || '';
                var hash = to.hash || '';
                window.location.href = "".concat(prefixedPath1).concat(search).concat(hash);
            }
        }
    }, []);
    // Router context가 없으면 fallback 함수 반환
    if (!hasRouterContext) {
        return fallbackNavigate;
    }
    // Router context가 있으면 useNavigate 사용
    var navigate = useNavigate();
    var prefixedNavigate = useCallback(function(to, options) {
        // 숫자 인자(예: navigate(-1))는 prefix 없이 그대로 전달
        if (typeof to === 'number') {
            navigate(to);
            return;
        }
        // 문자열 경로에 prefix 적용
        if (typeof to === 'string') {
            var prefixedPath = getRoutePath(to);
            navigate(prefixedPath, options);
        } else if ((typeof to === "undefined" ? "undefined" : _type_of(to)) === 'object' && to.pathname) {
            // Path 객체의 경우 pathname에만 prefix 적용
            var prefixedPath1 = getRoutePath(to.pathname);
            navigate(_object_spread_props(_object_spread({}, to), {
                pathname: prefixedPath1
            }), options);
        } else {
            // 기타 경우는 그대로 전달
            navigate(to, options);
        }
    }, [
        navigate
    ]);
    return prefixedNavigate;
}
